import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMerchantStaff, updateMerchantOperatorStatus } from "../../api/merchantStaffApi";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./staff-management.scss";

const formatPhone = (phone) => phone ? `+${phone}` : "—";

export default function StaffManagementPage() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [role, setRole] = useState("All Roles");
  const [status, setStatus] = useState("All Statuses");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    getMerchantStaff()
      .then((data) => {
        setStaff(data.staff || []);
        setCanManage(Boolean(data.can_manage_operators));
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  const filteredStaff = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    return staff.filter((member) => {
      const outletNames = member.type === "owner"
        ? ["All Outlets"]
        : member.outlets.map((outlet) => outlet.name);
      const matchesKeyword = !normalizedKeyword || [member.name, member.phone, ...outletNames]
        .join(" ").toLowerCase().includes(normalizedKeyword);
      const matchesRole = role === "All Roles" || member.role === role;
      const displayStatus = Number(member.status) === 1 ? "Active" : "Inactive";
      return matchesKeyword && matchesRole && (status === "All Statuses" || displayStatus === status);
    });
  }, [keyword, role, staff, status]);

  const toggleStatus = async (member) => {
    const nextStatus = Number(member.status) === 1 ? 0 : 1;
    setUpdatingId(member.id);
    setError("");
    try {
      const updated = await updateMerchantOperatorStatus(member.id, nextStatus);
      setStaff((current) => current.map((item) => (
        item.type === "operator" && item.id === member.id ? updated : item
      )));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <ModulePageShell className="staff-management">
      <ModuleHeader
        bleed
        title="Staff Management"
        description="Manage merchant owners and operators through role-based permissions."
        actions={
          canManage ? (
            <ModuleHeaderAction onClick={() => navigate("/staff/create")}>
              Add Operator
            </ModuleHeaderAction>
          ) : null
        }
      />

      <section className="staff-management__card">
        <div className="staff-management__card-header">
          <div>
            <h3>Staff Accounts</h3>
            <p>Phone numbers are used for merchant portal login. Merchant Owner accounts are created and managed by Platform Admin.</p>
          </div>
          <span className="staff-management__count">{filteredStaff.length} Staff</span>
        </div>

        {error ? <div className="staff-management__empty">{error}</div> : null}

        <div className="staff-management__filters">
          <label><span>Search</span><input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="Search by name, phone or outlet" /></label>
          <label><span>Role</span><select value={role} onChange={(event) => setRole(event.target.value)}><option>All Roles</option><option>Owner</option><option>Operator</option></select></label>
          <label><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option>All Statuses</option><option>Active</option><option>Inactive</option></select></label>
        </div>

        <div className="staff-management__table">
          <div className="staff-management__table-head"><span>Staff</span><span>Role</span><span>Outlet Access</span><span>Status</span><span>Created By</span><span>Actions</span></div>
          {filteredStaff.map((member) => {
            const active = Number(member.status) === 1;
            const outlets = member.type === "owner"
              ? ["All Outlets"]
              : member.outlets.map((outlet) => outlet.name);
            return (
              <div className="staff-management__table-row" key={`${member.type}-${member.id}`}>
                <div className="staff-management__staff-cell"><strong>{member.name}</strong><small>{formatPhone(member.phone)}</small></div>
                <span><b className={`staff-management__role staff-management__role--${member.role.toLowerCase()}`}>{member.role}</b></span>
                <div className="staff-management__outlets"><strong>{outlets.slice(0, 2).join(", ") || "No outlet assigned"}</strong>{outlets.length > 2 ? <small>+{outlets.length - 2} more</small> : null}</div>
                <span><b className={`staff-management__status staff-management__status--${active ? "active" : "inactive"}`}>{active ? "Active" : "Inactive"}</b></span>
                <span>{member.created_by}</span>
                <div className="staff-management__actions">
                  <button type="button" onClick={() => navigate(`/staff/${member.type}/${member.id}/edit`)}>{member.type === "owner" ? "View" : "Edit"}</button>
                  {canManage && member.type === "operator" ? (
                    <button type="button" disabled={updatingId === member.id} className={active ? "staff-management__action-danger" : ""} onClick={() => toggleStatus(member)}>
                      {updatingId === member.id ? "Saving..." : active ? "Deactivate" : "Activate"}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {loading ? <div className="staff-management__empty">Loading staff accounts...</div> : null}
        {!loading && !filteredStaff.length ? <div className="staff-management__empty">No staff accounts match the selected filters.</div> : null}
      </section>
    </ModulePageShell>
  );
}
